import { DATE_FORMATS } from "@/constants";
import { useDeleteContacts, useGetContacts } from "@/hooks/query-hooks/useContacts";
import { Divider, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { TableRowSelection } from "antd/es/table/interface";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { AnimatedComponent, AntdTable, DeleteConfirmationModal, PageTitle } from "../common";
import { AddNewContactModal } from "./AddNewContactModal";
import { EditContactModal } from "./EditContactModal";

export function Contacts() {

    const { contacts, isLoading } = useGetContacts();
    const { mutate, isLoading: isDeleting } = useDeleteContacts();

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection: TableRowSelection<Contact> = {
        selectedRowKeys,
        onChange: onSelectChange,
        type: "checkbox"
    }

    const onDeleteMultipleContacts = () => {
        if (selectedRowKeys.length === 0) return;
        mutate(selectedRowKeys as number[], {
            onSuccess: () => {
                setSelectedRowKeys([]);
            }
        })
    }

    const columns: ColumnsType<Contact> = [
        {
            title: "Title",
            dataIndex: "title",
            key: "title",
        },
        {
            title: "Driver Name",
            dataIndex: "driverName",
            key: "driverName",
        },
        {
            title: "Driver Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
        },
        {
            title: "Pickup Date",
            dataIndex: "pickUpDate",
            key: "pickUpDate",
            render: (value: string) => dayjs(value).format(DATE_FORMATS.DAY_MONTH_FORMAT),
        },
        {
            title: "Pickup Time",
            dataIndex: "pickUpTime",
            key: "pickUpTime",
            render: (time) => dayjs(time, DATE_FORMATS.REQUEST_TIME_FORMAT).format(DATE_FORMATS.DEFAULT_DISPLAY_TIME_FORMAT)
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <>
                    <Space>
                        <EditContactModal
                            contact={record}
                        />
                        <DeleteConfirmationModal
                            onDelete={() => mutate([record.id])}
                            isDeleting={isDeleting}
                        />
                    </Space>
                </>
            ),
            fixed: "right"
        }
    ]

    return (
        <AnimatedComponent>
            <Helmet>
                <title>အဆက်အသွယ်များ</title>
            </Helmet>
            <div
                className="flex items-center justify-between gap-2"
            >
                <PageTitle>
                    အဆက်အသွယ်များ
                </PageTitle>

                <Space
                    size={"large"}
                >
                    <DeleteConfirmationModal
                        onDelete={onDeleteMultipleContacts}
                        isDeleting={isDeleting}
                        title="Delete Contacts"
                        description="Are you sure you want to delete the selected contacts?"
                        buttonProps={{
                            type: "primary",
                            size: "large",
                            className: "!font-poppins",
                            hidden: selectedRowKeys.length === 0
                        }}
                    />

                    <AddNewContactModal />
                </Space>
            </div>
            <Divider />
            <AntdTable
                columns={columns}
                dataSource={contacts ?? []}
                rowKey={(record) => record.id}
                loading={isLoading}
                rowSelection={rowSelection}
            />
        </AnimatedComponent>
    )
}
